import { useEffect, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { name: "", vat: "", email: "", phone: "", address: "", notes: "",
  tax_code: "", sdi_code: "", sdi_pec: "", street: "", zip: "", city: "", province: "",
  country: "IT" };

const Clienti = () => {
  const { t, lang } = useI18n();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/clients", { params: { q } }).then(({ data }) => setClients(data));

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? t("clients.updated") : t("clients.created"));
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    await api.delete(`/clients/${toDelete.id}`);
    setToDelete(null);
    toast.success(t("clients.deleted"));
    load();
  };

  const field = (key, label, type = "text") => (
    <div>
      <Label htmlFor={key}>{label}</Label>
      <Input
        id={key}
        data-testid={`client-${key}`}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        required={key === "name"}
        className="mt-1.5 focus:ring-2 focus:ring-blue-600"
      />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="client-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("clients.searchPlaceholder")}
            className="pl-9 focus:ring-2 focus:ring-blue-600"
          />
        </div>
        <Button
          data-testid="new-client-button"
          onClick={() => {
            setEditing(null);
            setForm(EMPTY);
            setOpen(true);
          }}
          className="gap-2 bg-blue-600 text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> {t("clients.new")}
        </Button>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">{t("clients.name")}</th>
              <th className="px-4 py-3">{t("common.vatId")}</th>
              <th className="px-4 py-3">{t("common.email")}</th>
              <th className="px-4 py-3">{t("common.phone")}</th>
              <th className="px-4 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="clients-table">
            {clients.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                  {t("clients.empty")}
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 transition-colors duration-200 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="font-mono-num px-4 py-3 text-slate-600">{c.vat || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                <td className="font-mono-num px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button
                      data-testid={`edit-client-${c.id}`}
                      onClick={() => {
                        setEditing(c.id);
                        setForm({ ...EMPTY, ...c });
                        setOpen(true);
                      }}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`delete-client-${c.id}`}
                      onClick={() => setToDelete(c)}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="client-dialog"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>{editing ? t("clients.edit") : t("clients.new")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {field("name", t("clients.name"))}
            <div className="grid gap-4 sm:grid-cols-2">
              {field("vat", t("common.vatId"))}
              {field("phone", t("common.phone"))}
            </div>
            {field("email", t("common.email"), "email")}
            {field("address", t("common.address"))}
            {field("notes", t("common.notes"))}
            {lang === "it" && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold">Fattura elettronica (SDI)</p>
              <p className="mt-1 text-xs text-slate-500">
                Serve solo per generare il file XML da inviare allo SDI. Puoi compilarlo dopo.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {field("tax_code", "Codice fiscale")}
                {field("sdi_code", "Codice destinatario")}
                {field("street", "Indirizzo")}
                {field("zip", "CAP")}
                {field("city", "Comune")}
                {field("province", "Provincia")}
              </div>
              <div className="mt-3">{field("sdi_pec", "PEC (se non ha il codice)")}</div>
            </div>
            )}
            <DialogFooter>
              <Button
                data-testid="save-client"
                type="submit"
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clients.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("clients.deleteHint")} <b>{toDelete?.name}</b>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-client-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-client-confirm"
              onClick={remove}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clienti;
